import {
  Autocomplete,
  DialogActions,
  DialogContent,
  MenuItem,
} from '@mui/material';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import breakpoints from '../../../design-system/breakpoints';
import Variables from '../../../design-system/variables';
import OvSelect from '../atoms/OvSelect';
import Colours from '../../../design-system/colours';
import { HealthGoalType } from '../../../common/model/dto/health-goal-type.enum';
import { FieldArray } from 'formik';
import OvTextField from '../atoms/OvTextField';
import OvButton from '../atoms/OvButton';
import { symptomDefinitions } from '../../../common/model/type/symptom.type';

interface OvNotificationContentConditionsStepProps {
  formik: any;
  onBack: () => void;
  onCancel: () => void;
  submitButtonCta: string;
}

const OvNotificationContentConditionsStep: FC<
  OvNotificationContentConditionsStepProps
> = ({ formik, onBack, onCancel, submitButtonCta }) => {
  const { t } = useTranslation();

  return (
    <form onSubmit={formik.handleSubmit}>
      <StyledDialogContent>
        <>
          <StyledLabel>
            {t('notificationContents.publish.healthGoal')}
          </StyledLabel>
          <StyledSelect
            multiple
            labelId="health-goal"
            value={formik.values.event_conditions.health_goal || []}
            onChange={(event) =>
              formik.setFieldValue(
                'event_conditions.health_goal',
                event.target.value
              )
            }
            {...formik.getFieldProps('event_conditions.health_goal')}
            displayEmpty
            renderValue={(selected: any) => {
              if (selected.length === 0) {
                return <em>{t('common.actions.notSelected')}</em>;
              }
              return selected.join(', ');
            }}
          >
            <StyledMenuItem value={''} disabled>
              <em>{t('common.actions.notSelected')}</em>
            </StyledMenuItem>
            {[
              HealthGoalType.TRYING_TO_CONCEIVE,
              HealthGoalType.PERIMENOPAUSE_TRACKING,
              HealthGoalType.HORMONE_OPTIMIZATION,
            ].map((healthGoal) => (
              <StyledMenuItem key={healthGoal} value={healthGoal}>
                {healthGoal}
              </StyledMenuItem>
            ))}
          </StyledSelect>
          <StyledLabel>{t('notificationContents.publish.os')}</StyledLabel>
          <StyledSelect
            labelId="os"
            {...formik.getFieldProps('event_conditions.os')}
            displayEmpty
          >
            <StyledMenuItem value={''}>
              <em>{t('common.actions.notSelected')}</em>
            </StyledMenuItem>
            {['iOS', 'Android'].map((osOption) => (
              <StyledMenuItem key={osOption} value={osOption.toLowerCase()}>
                {osOption}
              </StyledMenuItem>
            ))}
          </StyledSelect>
        </>
        <FieldArray name="event_conditions.symptom_conditions">
          {(conditionSetHelpers) => (
            <>
              {formik.values.event_conditions.symptom_conditions.map(
                (setObj: any, setIndex: number) => {
                  const isNotLast =
                    setIndex <
                    formik.values.event_conditions.symptom_conditions.length -
                      1;
                  return (
                    <React.Fragment key={setIndex}>
                      <ConditionSetContainer>
                        <StyledOvButton
                          onClick={() => conditionSetHelpers.remove(setIndex)}
                          style={{
                            margin: '1rem',
                            alignSelf: 'flex-end',
                          }}
                        >
                          {t('notificationContents.symptoms.removeSet')}
                        </StyledOvButton>
                        <FieldArray
                          name={`event_conditions.symptom_conditions.${setIndex}.pairs`}
                        >
                          {(pairsHelpers) => (
                            <div>
                              {setObj?.pairs?.map(
                                (pair: any, pairIndex: number) => {
                                  const isNotLastPair =
                                    pairIndex < setObj.pairs.length - 1;
                                  const selectedSymptom =
                                    symptomDefinitions.find(
                                      (def) => def.propertyName === pair.key
                                    );
                                  return (
                                    <React.Fragment key={pairIndex}>
                                      <PairRow>
                                        <StyledSelectContainer>
                                          <StyledLabel>{'Symptom'}</StyledLabel>
                                          <StyledAutocomplete
                                            options={symptomDefinitions}
                                            getOptionLabel={(option: any) =>
                                              option.label
                                            }
                                            isOptionEqualToValue={(
                                              option: any,
                                              value: any
                                            ) =>
                                              option.propertyName ===
                                              value.propertyName
                                            }
                                            value={
                                              symptomDefinitions.find(
                                                (def) =>
                                                  def.propertyName === pair.key
                                              ) || null
                                            }
                                            onChange={(
                                              event,
                                              newValue: any
                                            ) => {
                                              formik.setFieldValue(
                                                `event_conditions.symptom_conditions.${setIndex}.pairs.${pairIndex}.key`,
                                                newValue
                                                  ? newValue.propertyName
                                                  : ''
                                              );
                                            }}
                                            clearOnEscape
                                            renderInput={(params) => (
                                              <StyledHalfLengthTextField
                                                {...params}
                                                placeholder="Select a symptom..."
                                              />
                                            )}
                                          />
                                        </StyledSelectContainer>
                                        {selectedSymptom &&
                                          selectedSymptom.type === 'select' && (
                                            <StyledSelectContainer>
                                              <StyledLabel>
                                                {'Severity'}
                                              </StyledLabel>
                                              <StyledHalfLengthSelect
                                                name={`event_conditions.symptom_conditions.${setIndex}.pairs.${pairIndex}.value`}
                                                labelId={`symptom-severity-${pairIndex}`}
                                                onChange={formik.handleChange}
                                                value={pair.value}
                                                displayEmpty
                                              >
                                                <StyledMenuItem value={''}>
                                                  <em>
                                                    {t(
                                                      'common.actions.notSelected'
                                                    )}
                                                  </em>
                                                </StyledMenuItem>
                                                <StyledMenuItem value={'any'}>
                                                  <em>
                                                    {t(
                                                      'notificationContents.symptoms.any'
                                                    )}
                                                  </em>
                                                </StyledMenuItem>
                                                {selectedSymptom?.options?.map(
                                                  (severity) => (
                                                    <StyledMenuItem
                                                      key={`${severity.value}-${pairIndex}`}
                                                      value={severity.value}
                                                    >
                                                      {severity.value}
                                                    </StyledMenuItem>
                                                  )
                                                )}
                                              </StyledHalfLengthSelect>
                                            </StyledSelectContainer>
                                          )}
                                        {selectedSymptom &&
                                          selectedSymptom.type === 'number' && (
                                            <StyledNumberInputContainer>
                                              <StyledHalfLengthSelect
                                                name={`event_conditions.symptom_conditions.${setIndex}.pairs.${pairIndex}.operator`}
                                                value={pair.operator ?? ''}
                                                onChange={formik.handleChange}
                                                displayEmpty
                                              >
                                                <StyledMenuItem value={''}>
                                                  <em>
                                                    {t(
                                                      'common.actions.notSelected'
                                                    )}
                                                  </em>
                                                </StyledMenuItem>
                                                <StyledMenuItem value=">">
                                                  {t(
                                                    'notificationContents.symptoms.greaterThan'
                                                  )}
                                                </StyledMenuItem>
                                                <StyledMenuItem value="<">
                                                  {t(
                                                    'notificationContents.symptoms.lowerThan'
                                                  )}
                                                </StyledMenuItem>
                                              </StyledHalfLengthSelect>
                                              <StyledHalfLengthTextField
                                                name={`event_conditions.symptom_conditions.${setIndex}.pairs.${pairIndex}.value`}
                                                type="number"
                                                fullWidth
                                                autoComplete="off"
                                                onChange={formik.handleChange}
                                                value={pair.value}
                                                label={'Severity'}
                                                InputProps={{
                                                  inputProps: {
                                                    min: selectedSymptom.min,
                                                    max: selectedSymptom.max,
                                                    step: selectedSymptom.step,
                                                  },
                                                }}
                                              />
                                            </StyledNumberInputContainer>
                                          )}
                                        {selectedSymptom &&
                                          selectedSymptom.type ===
                                            'boolean' && (
                                            <StyledSelectContainer>
                                              <StyledHalfLengthSelect
                                                name={`event_conditions.symptom_conditions.${setIndex}.pairs.${pairIndex}.value`}
                                                labelId={`symptom-severity-${pairIndex}`}
                                                onChange={formik.handleChange}
                                                value={pair.value}
                                                displayEmpty
                                              >
                                                <StyledMenuItem value={''}>
                                                  <em>
                                                    {t(
                                                      'common.actions.notSelected'
                                                    )}
                                                  </em>
                                                </StyledMenuItem>
                                                <StyledMenuItem value={'true'}>
                                                  <em>
                                                    {t('common.actions.yes')}
                                                  </em>
                                                </StyledMenuItem>
                                                <StyledMenuItem value={'false'}>
                                                  <em>
                                                    {t('common.actions.no')}
                                                  </em>
                                                </StyledMenuItem>
                                              </StyledHalfLengthSelect>
                                            </StyledSelectContainer>
                                          )}
                                        <StyledOvButton
                                          onClick={() =>
                                            pairsHelpers.remove(pairIndex)
                                          }
                                          style={{
                                            alignSelf: 'flex-end',
                                            marginBottom: '1px',
                                          }}
                                        >
                                          -
                                        </StyledOvButton>
                                      </PairRow>
                                      {isNotLastPair && (
                                        <StyledLabel
                                          style={{ margin: '0.5rem' }}
                                        >
                                          {t(
                                            'notificationContents.symptoms.and'
                                          )}
                                        </StyledLabel>
                                      )}
                                    </React.Fragment>
                                  );
                                }
                              )}
                              <StyledOvButton
                                onClick={() =>
                                  pairsHelpers.push({
                                    key: '',
                                    value: '',
                                    operator: '',
                                  })
                                }
                                style={{ margin: '1rem 0.25rem' }}
                              >
                                +
                              </StyledOvButton>
                            </div>
                          )}
                        </FieldArray>
                      </ConditionSetContainer>
                      {isNotLast && (
                        <StyledLabel style={{ margin: '0.5rem' }}>
                          {t('notificationContents.symptoms.or')}
                        </StyledLabel>
                      )}
                    </React.Fragment>
                  );
                }
              )}
              <StyledOvButton
                onClick={() =>
                  conditionSetHelpers.push({
                    pairs: [{ key: '', value: '', operator: '' }],
                  })
                }
                style={{ marginLeft: '0' }}
              >
                {t('notificationContents.symptoms.addConditionSet')}{' '}
              </StyledOvButton>
            </>
          )}
        </FieldArray>
        <StyledDialogActions>
          <StyledLightOvButton onClick={onCancel}>
            {t('common.actions.cancel')}
          </StyledLightOvButton>
          <StyledLightOvButton onClick={onBack}>
            {t('common.actions.back')}
          </StyledLightOvButton>
          <StyledOvButton type="submit">{submitButtonCta}</StyledOvButton>
        </StyledDialogActions>
      </StyledDialogContent>
    </form>
  );
};

export default OvNotificationContentConditionsStep;

const StyledDialogContent = styled(DialogContent)`
  && {
    width: 100%;
    min-width: 31.25rem !important;
    padding: 0.5rem 1.5rem !important;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 0.75rem;

    @media (min-width: ${breakpoints.sm}) {
      width: 34rem;
    }
  }
`;

const StyledLabel = styled.label`
  font-size: ${Variables.fontSizes.MEDIUM};
  margin-right: 0.5rem;
`;

const StyledSelect = styled(OvSelect)`
  && {
    max-width: none;
    height: 3rem;

    &.MuiInputBase-root {
      font-size: 0.875rem;
      border-color: ${Colours.OV_LIGHT_GRAY};
      width: 100%;

      .MuiOutlinedInput-notchedOutline {
        border-color: ${Colours.OV_BASE};
      }
    }
  }
`;

const StyledMenuItem = styled(MenuItem)`
  && {
    &.MuiButtonBase-root {
      display: flex;
      justify-content: flex-start;
      padding: 0.375rem 1rem;
      font-size: ${Variables.fontSizes.MEDIUM};
    }

    &.Mui-selected {
      background-color: ${Colours.OV_BORDER_COLOR_V3};

      &:hover {
        background-color: ${Colours.OV_DISABLED};
      }
    }
  }
`;

const ConditionSetContainer = styled.div`
  width: 100%;
  border: 1px solid ${Colours.OV_BASE};
  border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
  box-shadow: ${Variables.boxShadow.alternateBox};
  display: flex;
  flex-direction: column;
`;

const StyledHalfLengthTextField = styled(OvTextField)`
  && {
    flex: 1 0 31%;
    margin: 0 10px 1px 10px;

    .MuiFormHelperText-root.Mui-error {
      background: ${Colours.OV_WHITE};
      margin: 0;
      padding: 0.25rem 0.75rem;
    }
    .MuiInputLabel-formControl {
      top: -0.1rem;
      opacity: 0.6;
    }
  }
`;

const StyledLightOvButton = styled(OvButton)`
  && {
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    padding: 0 1rem;
    background-color: ${Colours.OV_WHITE};
    border: 1px solid rgba(1, 39, 70, 0.5);
    color: ${Colours.OV_BASE};
    font-weight: bold;
    text-transform: none;
    max-width: fit-content;
    &:hover {
      border: 1px solid ${Colours.OV_BASE};
      background-color: rgba(1, 39, 70, 0.04);
    }
  }
`;

const StyledOvButton = styled(OvButton)`
  && {
    padding: 0 1rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    margin-right: 0.75rem;
    transition: none;
    font-weight: bold;
    text-transform: none;
    margin-left: 1rem;
    max-width: fit-content;

    &:last-child {
      margin-right: 0;
    }
  }
`;

const StyledDialogActions = styled(DialogActions)`
  && {
    margin-top: 2rem;
    padding: 0 0 0.5rem 1.5rem;
  }
`;

const PairRow = styled.div`
  display: flex;
  gap: 0.15rem;
  margin: 0.25rem;
  align-items: flex-end;
  justify-content: space-between;
`;

const StyledSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 40%;
`;

const StyledNumberInputContainer = styled.div`
  display: flex;
`;

const StyledHalfLengthSelect = styled(OvSelect)`
  && {
    flex: 1 0 40%;
    width: 100%;
    padding: 10px 10px;

    &.MuiInputBase-root {
      font-size: 0.875rem;
      border-color: ${Colours.OV_LIGHT_GRAY};

      .MuiOutlinedInput-notchedOutline {
        border-color: ${Colours.OV_BASE};
      }
    }
  }
`;

const StyledAutocomplete = styled(Autocomplete)`
  && {
    flex: 1 0 40%;
    width: 100%;
    margin-bottom: 1px;

    .MuiFormControl-root {
      margin: 0;

      .MuiInputBase-root {
        padding: 0;
      }
    }

    .MuiOutlinedInput-root {
      font-size: 0.875rem;
      border-color: ${Colours.OV_LIGHT_GRAY};
      margin: 0;

      .MuiOutlinedInput-notchedOutline {
        border-color: ${Colours.OV_BASE};
      }
    }
  }
`;
